import React from 'react';
import { 
  AlertTriangle, 
  GitBranch, 
  Eye, 
  Calendar, 
  Clock, 
  CheckCircle,
  ArrowRight,
  ExternalLink
} from 'lucide-react';
import Badge from '../../../components/ui/Badge';
import Button from '../../../components/ui/Button';
import { 
  Dependency, 
  DependencyType, 
  DependencyStatus, 
  DependencySeverity 
} from '../../../types';

interface DependencyDetailModalProps {
  dependency: Dependency;
  onClose: () => void;
}

export const DependencyDetailModal: React.FC<DependencyDetailModalProps> = ({
  dependency,
  onClose,
}) => {
  const getSeverityColor = (severity: DependencySeverity) => {
    switch (severity) {
      case DependencySeverity.CRITICAL:
        return 'danger';
      case DependencySeverity.HIGH:
        return 'warning';
      case DependencySeverity.MEDIUM:
        return 'info';
      case DependencySeverity.LOW:
        return 'success';
      default:
        return 'secondary';
    }
  };

  const getStatusColor = (status: DependencyStatus) => {
    switch (status) {
      case DependencyStatus.OPEN:
        return 'danger';
      case DependencyStatus.IN_PROGRESS:
        return 'info';
      case DependencyStatus.RESOLVED:
        return 'success';
      case DependencyStatus.CANCELLED:
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  const getTypeIcon = (type: DependencyType) => {
    switch (type) {
      case DependencyType.BLOCKING:
        return <AlertTriangle className="w-5 h-5" />;
      case DependencyType.ENABLING:
        return <GitBranch className="w-5 h-5" />;
      case DependencyType.INFORMATIONAL:
        return <Eye className="w-5 h-5" />;
      default:
        return <GitBranch className="w-5 h-5" />;
    }
  };

  const formatEntityType = (entityType: string) => {
    return entityType.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRiskIndicator = () => {
    if (dependency.riskLevel === 'OVERDUE') {
      return (
        <div className="flex items-center space-x-2 text-status-error">
          <Clock className="w-4 h-4" />
          <span className="font-medium">Overdue</span>
          {dependency.daysUntilDue && (
            <span className="text-sm">({Math.abs(dependency.daysUntilDue)} days)</span>
          )}
        </div>
      );
    }
    if (dependency.riskLevel === 'DUE_SOON') {
      return (
        <div className="flex items-center space-x-2 text-status-warning">
          <Clock className="w-4 h-4" />
          <span className="font-medium">Due Soon</span>
          {dependency.daysUntilDue && (
            <span className="text-sm">({dependency.daysUntilDue} days left)</span>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start space-x-4">
        <div className="flex-shrink-0 p-3 bg-muted rounded-lg">
          {getTypeIcon(dependency.type)}
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-semibold text-foreground">{dependency.name}</h2>
          {dependency.description && (
            <p className="text-foreground mt-1">{dependency.description}</p>
          )}
          <div className="flex items-center space-x-3 mt-3">
            <Badge variant={getSeverityColor(dependency.severity)}>
              {dependency.severity}
            </Badge>
            <Badge variant={getStatusColor(dependency.status)}>
              {dependency.status.replace('_', ' ')}
            </Badge>
            <Badge variant="secondary" className="flex items-center space-x-1">
              {getTypeIcon(dependency.type)}
              <span>{dependency.type}</span>
            </Badge>
          </div>
        </div>
      </div>

      {/* Risk Indicator */}
      {getRiskIndicator() && (
        <div className="bg-status-warning/10 border border-yellow-500/20 rounded-lg p-4">
          {getRiskIndicator()}
        </div>
      )}

      {/* Relationship */}
      <div className="bg-muted/50 rounded-lg p-4">
        <h3 className="text-lg font-medium text-foreground mb-4">Dependency Relationship</h3>
        <div className="flex items-center space-x-4">
          <div className="flex-1 text-center">
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="text-sm text-muted-foreground mb-1">Source Entity</div>
              <div className="font-medium text-foreground">{dependency.sourceName || 'Unknown'}</div>
              <div className="text-xs text-muted-foreground mt-1">
                {formatEntityType(dependency.sourceType)}
              </div>
            </div>
          </div>
          
          <div className="flex-shrink-0">
            <ArrowRight className="w-6 h-6 text-muted-foreground" />
          </div>
          
          <div className="flex-1 text-center">
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="text-sm text-muted-foreground mb-1">Target Entity</div>
              <div className="font-medium text-foreground">{dependency.targetName || 'Unknown'}</div>
              <div className="text-xs text-muted-foreground mt-1">
                {formatEntityType(dependency.targetType)}
              </div>
            </div>
          </div>
        </div>
        
        <div className="text-center mt-4 text-sm text-muted-foreground">
          <span className="font-medium text-foreground">{dependency.targetName}</span> depends on{' '}
          <span className="font-medium text-foreground">{dependency.sourceName}</span>
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-muted/50 rounded-lg p-4">
        <h3 className="text-lg font-medium text-foreground mb-4">Timeline</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Created:</span>
            <span className="text-foreground">{formatDateTime(dependency.createdAt)}</span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Last Updated:</span>
            <span className="text-foreground">{formatDateTime(dependency.updatedAt)}</span>
          </div>
          
          {dependency.expectedDate && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Expected Resolution:</span>
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span className="text-foreground">{formatDate(dependency.expectedDate)}</span>
              </div>
            </div>
          )}
          
          {dependency.resolvedDate && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Resolved:</span>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-4 h-4 text-status-success" />
                <span className="text-foreground">{formatDateTime(dependency.resolvedDate)}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Impact Assessment */}
      <div className="bg-muted/50 rounded-lg p-4">
        <h3 className="text-lg font-medium text-foreground mb-4">Impact Assessment</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Dependency Type:</span>
            <div className="flex items-center space-x-2">
              {getTypeIcon(dependency.type)}
              <span className="text-foreground">{dependency.type}</span>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Severity Level:</span>
            <Badge variant={getSeverityColor(dependency.severity)}>
              {dependency.severity}
            </Badge>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Current Status:</span>
            <Badge variant={getStatusColor(dependency.status)}>
              {dependency.status.replace('_', ' ')}
            </Badge>
          </div>
        </div>
        
        {dependency.type === DependencyType.BLOCKING && (
          <div className="mt-4 p-3 bg-status-error/10 border border-status-error/30 rounded-lg">
            <div className="flex items-center space-x-2 text-status-error">
              <AlertTriangle className="w-4 h-4" />
              <span className="font-medium">Blocking Dependency</span>
            </div>
            <p className="text-status-error text-sm mt-1">
              This dependency is blocking progress on {dependency.targetName}. 
              Resolution is required before work can proceed.
            </p>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-border">
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
        <Button
          variant="outline"
          className="flex items-center space-x-2"
          onClick={() => {
            // Navigate to source entity (would need routing implementation)
            console.log('Navigate to source entity:', dependency.sourceType, dependency.sourceId);
          }}
        >
          <ExternalLink className="w-4 h-4" />
          <span>View Source</span>
        </Button>
        <Button
          variant="outline"
          className="flex items-center space-x-2"
          onClick={() => {
            // Navigate to target entity (would need routing implementation)
            console.log('Navigate to target entity:', dependency.targetType, dependency.targetId);
          }}
        >
          <ExternalLink className="w-4 h-4" />
          <span>View Target</span>
        </Button>
      </div>
    </div>
  );
};